import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  acknowledgeMindMapNotifications,
  createMindMap,
  deleteMindMap,
  getAccountCapabilities,
  listMindMaps,
  removeSavedMindMapLink,
  saveMindMapLink,
  UpgradeRequiredError,
  updateMindMapArchive,
} from '../../utils/mindmap/apiClient';
import UpgradeDialog from './UpgradeDialog';

const roleLabel = (map) => {
  if (map.accessSource === 'owner') return 'Owner';
  if (map.accessSource === 'saved_link') {
    return map.accessRole === 'editor' ? 'Saved · Edit' : 'Saved · View';
  }
  return map.accessRole === 'editor' ? 'Editor' : 'Viewer';
};

const mapTitle = (map) => map.title?.trim() || 'Untitled map';

const MapsIcon = () => (
  <svg
    aria-hidden="true"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="4" y="4" width="13" height="13" rx="2" />
    <path d="M8 20h10a2 2 0 0 0 2-2V8" />
  </svg>
);

const ImportIcon = () => (
  <svg
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </svg>
);

const TrashIcon = () => (
  <svg
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4h8v2" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v5" />
    <path d="M14 11v5" />
  </svg>
);

const MapsMenu = ({ currentMap, isDarkMode, mapFileActions, onOpen }) => {
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [maps, setMaps] = useState([]);
  const [accountCapabilities, setAccountCapabilities] = useState(null);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [deletingMapId, setDeletingMapId] = useState(null);
  const [archivingMapId, setArchivingMapId] = useState(null);
  const [error, setError] = useState(null);
  const [savedCurrentMap, setSavedCurrentMap] = useState(false);
  const [upgradeRequest, setUpgradeRequest] = useState(null);

  const currentMapId = currentMap?.mindmapId || null;
  const canSaveCurrentMap = currentMapId
    && currentMap?.accessSource === 'link'
    && !savedCurrentMap;
  const canImportCurrentMap = typeof mapFileActions?.importIntoCurrentMap === 'function';
  const hasUnseenShareNotifications = maps.some(
    (map) => map.hasUnseenShareNotification === true
  );
  const enforcedUsage = accountCapabilities?.freeLimits?.enforced
    ? accountCapabilities.freeLimits.usage
    : null;
  const activeMapUsage = accountCapabilities?.freeLimits?.usage?.activeMaps || null;
  const isFreePlan = accountCapabilities?.plan?.key === 'free';
  const planLabel = accountCapabilities?.plan?.label || null;
  const activeMapLimitReached = Boolean(
    enforcedUsage
    && !activeMapUsage?.unlimited
    && activeMapUsage?.current >= activeMapUsage?.maximum
  );

  const palette = {
    panel: isDarkMode ? '#111827' : '#ffffff',
    panelMuted: isDarkMode ? '#1f2937' : '#f3f4f6',
    border: isDarkMode ? '#374151' : '#e5e7eb',
    text: isDarkMode ? '#f9fafb' : '#111827',
    muted: isDarkMode ? '#9ca3af' : '#6b7280',
    accent: isDarkMode ? '#60a5fa' : '#2563eb',
  };

  const loadMaps = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [result, account] = await Promise.all([
        listMindMaps(),
        getAccountCapabilities().catch(() => null),
      ]);
      setMaps(Array.isArray(result) ? result : []);
      setAccountCapabilities(account);
    } catch (loadError) {
      setError(loadError?.message || 'Could not load your maps.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMaps();
    const refreshOnFocus = () => loadMaps();
    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, [loadMaps]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const focusTimer = setTimeout(() => searchRef.current?.focus(), 0);
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    setSavedCurrentMap(currentMap?.accessSource === 'saved_link');
  }, [currentMapId, currentMap?.accessSource]);

  const filteredMaps = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const matching = normalized
      ? maps.filter((map) => mapTitle(map).toLowerCase().includes(normalized))
      : maps;
    return [...matching].sort((left, right) => (
      Number(Boolean(left.isArchived)) - Number(Boolean(right.isArchived))
    ));
  }, [maps, query]);

  const openMap = (id) => {
    if (typeof window === 'undefined') return;
    window.location.assign(`/?map=${encodeURIComponent(id)}`);
  };

  const acknowledgeNotifications = useCallback(async () => {
    setMaps((previous) => previous.map((map) => ({
      ...map,
      hasUnseenShareNotification: false,
    })));
    try {
      await acknowledgeMindMapNotifications();
    } finally {
      await loadMaps();
    }
  }, [loadMaps]);

  const handleCreate = async ({ skipPreflight = false } = {}) => {
    if (activeMapLimitReached && !skipPreflight) {
      setError(
        `Free includes ${activeMapUsage.maximum} active maps. Deactivate one below, or activate Beta Pro for unlimited active maps.`
      );
      setUpgradeRequest({
        capabilityKey: 'maps.active.max',
        returnLabel: 'Create new map',
        retry: () => handleCreate({ skipPreflight: true }),
      });
      return;
    }
    setIsCreating(true);
    setError(null);
    try {
      const created = await createMindMap(null);
      if (!created?.id) throw new Error('The new map could not be created.');
      openMap(created.id);
    } catch (createError) {
      if (createError instanceof UpgradeRequiredError) {
        setUpgradeRequest({
          capabilityKey: createError.upgradeContext.capabilityKey,
          returnLabel: 'Create new map',
          retry: () => handleCreate({ skipPreflight: true }),
        });
      }
      setError(createError?.message || 'The new map could not be created.');
      setIsCreating(false);
    }
  };

  const handleSaveCurrent = async () => {
    if (!currentMapId) return;
    setIsSaving(true);
    setError(null);
    try {
      await saveMindMapLink(currentMapId);
      setSavedCurrentMap(true);
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
        hashParams.delete('share');
        const nextHash = hashParams.toString();
        window.history.replaceState(
          null,
          '',
          `${url.pathname}${url.search}${nextHash ? `#${nextHash}` : ''}`
        );
      }
      await loadMaps();
    } catch (saveError) {
      setError(saveError?.message || 'This map could not be saved.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveSaved = async (event, map) => {
    event.stopPropagation();
    try {
      await removeSavedMindMapLink(map.id);
      setMaps((previous) => previous.filter((candidate) => candidate.id !== map.id));
      if (map.id === currentMapId) setSavedCurrentMap(false);
    } catch (removeError) {
      setError(removeError?.message || 'This map could not be removed.');
    }
  };

  const handleDelete = async (event, map) => {
    event.stopPropagation();
    const title = mapTitle(map);
    if (typeof window === 'undefined'
      || !window.confirm(`Delete “${title}”? This can’t be undone.`)) return;

    setDeletingMapId(map.id);
    setError(null);
    try {
      await deleteMindMap(map.id);
      if (map.id === currentMapId) {
        window.location.assign('/');
        return;
      }
      setMaps((previous) => previous.filter((candidate) => candidate.id !== map.id));
    } catch (deleteError) {
      setError(deleteError?.message || 'This map could not be deleted.');
    } finally {
      setDeletingMapId(null);
    }
  };

  const handleArchive = async (event, map) => {
    event?.stopPropagation();
    const nextArchived = !map.isArchived;
    setArchivingMapId(map.id);
    setError(null);
    try {
      const result = await updateMindMapArchive(map.id, nextArchived);
      if (nextArchived && map.id === currentMapId) {
        window.location.assign(`/?map=${encodeURIComponent(map.id)}`);
        return;
      }
      setMaps((previous) => previous.map((candidate) => (
        candidate.id === map.id
          ? {
            ...candidate,
            isArchived: result.archived,
            archivedAt: result.archivedAt,
          }
          : candidate
      )));
      setAccountCapabilities((previous) => {
        const usage = previous?.freeLimits?.usage;
        if (!usage?.activeMaps) return previous;
        const nextCurrent = Math.max(
          0,
          usage.activeMaps.current + (result.archived ? -1 : 1)
        );
        return {
          ...previous,
          freeLimits: {
            ...previous.freeLimits,
            usage: {
              ...usage,
              activeMaps: {
                ...usage.activeMaps,
                current: nextCurrent,
                overLimit: !usage.activeMaps.unlimited
                  && nextCurrent > usage.activeMaps.maximum,
              },
            },
          },
        };
      });
    } catch (archiveError) {
      if (archiveError instanceof UpgradeRequiredError) {
        setUpgradeRequest({
          capabilityKey: archiveError.upgradeContext.capabilityKey,
          returnLabel: `Activate ${mapTitle(map)}`,
          retry: () => handleArchive(null, map),
        });
      }
      setError(archiveError?.message || (
        nextArchived ? 'This map could not be deactivated.' : 'This map could not be activated.'
      ));
    } finally {
      setArchivingMapId(null);
    }
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'flex' }}>
      <button
        type="button"
        aria-label="Maps"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        title={hasUnseenShareNotifications ? 'Maps · New shared maps' : 'Maps'}
        onClick={(event) => {
          event.stopPropagation();
          const nextOpen = !isOpen;
          setIsOpen(nextOpen);
          if (nextOpen) {
            if (onOpen) onOpen();
            acknowledgeNotifications().catch(() => {
              // The next refresh will retry the durable acknowledgement.
            });
          }
        }}
        style={{
          position: 'relative',
          width: 28,
          height: 28,
          padding: 0,
          border: 'none',
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isDarkMode ? '#111827' : '#e5e7eb',
          color: palette.text,
          cursor: 'pointer',
        }}
      >
        <MapsIcon />
        {hasUnseenShareNotifications && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: -1,
              right: -1,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#ef4444',
              border: `2px solid ${palette.panel}`,
            }}
          />
        )}
      </button>
      {hasUnseenShareNotifications && (
        <span
          role="status"
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        >
          New shared maps available
        </span>
      )}

      {isOpen && (
        <section
          role="dialog"
          aria-label="My maps"
          style={{
            position: 'fixed',
            top: 52,
            right: 12,
            width: 'min(340px, calc(100vw - 24px))',
            padding: 12,
            borderRadius: 12,
            background: palette.panel,
            border: `1px solid ${palette.border}`,
            boxShadow: '0 18px 40px rgba(0,0,0,0.35)',
            color: palette.text,
            zIndex: 220,
            fontFamily: '"Quicksand", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <strong style={{ fontSize: 15 }}>My maps</strong>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                onClick={() => handleCreate()}
                disabled={isCreating}
                style={{
                  border: 0,
                  borderRadius: 7,
                  padding: '6px 9px',
                  background: palette.accent,
                  color: '#fff',
                  cursor: isCreating ? 'wait' : 'pointer',
                  fontWeight: 700,
                }}
              >
                {isCreating ? 'Creating…' : '+ New map'}
              </button>
              {canImportCurrentMap && (
                <button
                  type="button"
                  onClick={mapFileActions.importIntoCurrentMap}
                  title="Replace the current map with an imported file"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 5,
                    padding: '6px 9px',
                    border: `1px solid ${palette.border}`,
                    borderRadius: 7,
                    background: palette.panelMuted,
                    color: palette.text,
                    cursor: 'pointer',
                    fontWeight: 700,
                  }}
                >
                  <ImportIcon />
                  Import map
                </button>
              )}
            </div>
          </div>

          {canSaveCurrentMap && (
            <button
              type="button"
              onClick={handleSaveCurrent}
              disabled={isSaving}
              style={{
                width: '100%',
                marginTop: 10,
                padding: '8px 10px',
                border: `1px solid ${palette.accent}`,
                borderRadius: 8,
                background: 'transparent',
                color: palette.accent,
                cursor: isSaving ? 'wait' : 'pointer',
                textAlign: 'left',
                fontWeight: 700,
              }}
            >
              {isSaving ? 'Saving…' : 'Save current map to My maps'}
            </button>
          )}

          {isFreePlan && (
            <div
              style={{
                marginTop: 10,
                padding: '10px 11px',
                border: `1px solid ${palette.border}`,
                borderRadius: 9,
                background: palette.panelMuted,
                fontSize: 12,
                lineHeight: 1.45,
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
              }}
              >
                <strong>{planLabel || 'Your plan'}</strong>
                <button
                  type="button"
                  onClick={() => setUpgradeRequest({
                    capabilityKey: null,
                    returnLabel: null,
                    retry: null,
                  })}
                  style={{
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    color: palette.accent,
                    cursor: 'pointer',
                    font: 'inherit',
                    fontWeight: 700,
                  }}
                >
                  Compare plans
                </button>
              </div>
              {activeMapUsage && (
                <div style={{ marginTop: 3, color: palette.muted }}>
                  {activeMapUsage.unlimited
                    ? `${activeMapUsage.current} active maps · Unlimited`
                    : `${activeMapUsage.current} of ${activeMapUsage.maximum} active maps`}
                </div>
              )}
              <div style={{ marginTop: 4, color: palette.muted }}>
                Active maps can be edited and count toward your plan. Inactive maps stay
                safe and readable, but cannot be edited and do not count. Deleting is permanent.
              </div>
            </div>
          )}

          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search maps"
            aria-label="Search maps"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              marginTop: 10,
              padding: '8px 10px',
              borderRadius: 8,
              border: `1px solid ${palette.border}`,
              background: palette.panelMuted,
              color: palette.text,
              outline: 'none',
            }}
          />

          {error && (
            <div role="alert" style={{ marginTop: 8, fontSize: 12, color: '#fca5a5' }}>
              {error}
            </div>
          )}

          {enforcedUsage?.activeMaps?.overLimit && (
            <div
              role="status"
              style={{
                marginTop: 8,
                padding: '8px 10px',
                borderRadius: 8,
                background: isDarkMode ? 'rgba(245, 158, 11, 0.14)' : '#fffbeb',
                color: isDarkMode ? '#fcd34d' : '#92400e',
                fontSize: 12,
                lineHeight: 1.4,
              }}
            >
              You have {enforcedUsage.activeMaps.current} active maps. Your existing maps
              remain available. Deactivate maps until you are within the Free limit of{' '}
              {enforcedUsage.activeMaps.maximum}, or activate Beta Pro for unlimited maps.
            </div>
          )}

          {enforcedUsage?.namedEditors?.mapsOverLimit > 0 && (
            <div
              role="status"
              style={{
                marginTop: 8,
                padding: '8px 10px',
                borderRadius: 8,
                background: isDarkMode ? 'rgba(245, 158, 11, 0.14)' : '#fffbeb',
                color: isDarkMode ? '#fcd34d' : '#92400e',
                fontSize: 12,
                lineHeight: 1.4,
              }}
            >
              Existing collaborators keep access. Adding editors is paused on{' '}
              {enforcedUsage.namedEditors.mapsOverLimit}{' '}
              {enforcedUsage.namedEditors.mapsOverLimit === 1 ? 'map' : 'maps'} above
              the Free editor limit.
            </div>
          )}

          <div style={{
            marginTop: 8,
            maxHeight: 330,
            overflowY: 'auto',
            paddingRight: 4,
          }}
          >
            {isLoading && maps.length === 0 ? (
              <div style={{ padding: 14, color: palette.muted, fontSize: 13 }}>Loading maps…</div>
            ) : filteredMaps.length === 0 ? (
              <div style={{ padding: 14, color: palette.muted, fontSize: 13 }}>
                {query ? 'No maps match your search.' : 'No maps yet.'}
              </div>
            ) : filteredMaps.map((map) => {
              const isCurrent = map.id === currentMapId;
              const isNewShare = map.isNewShare === true;
              return (
                <div
                  key={map.id}
                  style={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    gap: 8,
                    alignItems: 'center',
                    boxSizing: 'border-box',
                    padding: '9px 12px 9px 8px',
                    border: 0,
                    borderRadius: 8,
                    background: isNewShare
                      ? (isDarkMode ? 'rgba(96, 165, 250, 0.18)' : 'rgba(37, 99, 235, 0.10)')
                      : (isCurrent ? palette.panelMuted : 'transparent'),
                    boxShadow: isNewShare ? `inset 3px 0 0 ${palette.accent}` : 'none',
                    color: palette.text,
                  }}
                >
                  <button
                    type="button"
                    aria-current={isCurrent ? 'page' : undefined}
                    onClick={() => openMap(map.id)}
                    style={{
                      minWidth: 0,
                      padding: 0,
                      border: 0,
                      background: 'transparent',
                      color: 'inherit',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <span
                      style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: 13,
                        fontWeight: isCurrent ? 700 : 600,
                      }}
                    >
                      {mapTitle(map)}
                    </span>
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 5,
                      marginTop: 3,
                      color: palette.muted,
                      fontSize: 11,
                    }}
                    >
                      {map.accessSource === 'owner' && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '1px 6px',
                            borderRadius: 999,
                            background: map.isArchived
                              ? (isDarkMode ? '#3f3f46' : '#e4e4e7')
                              : (isDarkMode ? 'rgba(34, 197, 94, 0.18)' : '#dcfce7'),
                            color: map.isArchived
                              ? palette.muted
                              : (isDarkMode ? '#86efac' : '#166534'),
                            fontWeight: 700,
                          }}
                        >
                          {map.isArchived ? 'Inactive' : 'Active'}
                        </span>
                      )}
                      <span>
                      {isNewShare && (
                        <strong style={{ color: palette.accent }}>New</strong>
                      )}
                      {isNewShare ? ' · ' : ''}{roleLabel(map)}{isCurrent ? ' · Current' : ''}
                      </span>
                    </span>
                  </button>
                  {map.accessSource === 'owner' && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      <button
                        type="button"
                        aria-label={`${map.isArchived ? 'Activate' : 'Deactivate'} ${mapTitle(map)}`}
                        title={map.isArchived
                          ? 'Activate this map so it can be edited'
                          : 'Deactivate this map to free an active-map slot'}
                        disabled={archivingMapId === map.id}
                        onClick={(event) => handleArchive(event, map)}
                        style={{
                          padding: '4px 6px',
                          border: `1px solid ${palette.border}`,
                          borderRadius: 6,
                          background: 'transparent',
                          color: palette.text,
                          fontSize: 10,
                          fontWeight: 700,
                          cursor: archivingMapId === map.id ? 'wait' : 'pointer',
                        }}
                      >
                        {archivingMapId === map.id
                          ? '…'
                          : (map.isArchived ? 'Activate' : 'Deactivate')}
                      </button>
                      <button
                        type="button"
                        aria-label={`Delete ${mapTitle(map)}`}
                        title="Delete map"
                        disabled={deletingMapId === map.id}
                        onClick={(event) => handleDelete(event, map)}
                        style={{
                          padding: 5,
                          border: 0,
                          background: 'transparent',
                          color: palette.muted,
                          fontSize: 16,
                          cursor: deletingMapId === map.id ? 'wait' : 'pointer',
                        }}
                      >
                        {deletingMapId === map.id ? '…' : <TrashIcon />}
                      </button>
                    </span>
                  )}
                  {map.accessSource === 'saved_link' && !isCurrent && (
                    <button
                      type="button"
                      aria-label={`Remove ${mapTitle(map)} from My maps`}
                      title="Remove from My maps"
                      onClick={(event) => handleRemoveSaved(event, map)}
                      style={{
                        padding: 5,
                        border: 0,
                        background: 'transparent',
                        color: palette.muted,
                        fontSize: 16,
                        cursor: 'pointer',
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}
      <UpgradeDialog
        isOpen={Boolean(upgradeRequest)}
        capabilityKey={upgradeRequest?.capabilityKey}
        returnLabel={upgradeRequest?.returnLabel}
        isDarkMode={isDarkMode}
        onClose={() => setUpgradeRequest(null)}
        onActivated={async (account) => {
          setAccountCapabilities(account);
          const retry = upgradeRequest?.retry;
          setUpgradeRequest(null);
          if (retry) await retry();
        }}
      />
    </div>
  );
};

export default MapsMenu;
